import React, { useEffect } from "react";
import { X } from "lucide-react";

function PlanItem({ item, onImport }) {
  const hasContent = item.unit_count > 0 || item.chapter_count > 0;
  return (
    <li className="flex items-center justify-between gap-3 border-b border-slate-100 py-3 last:border-0">
      <div className="min-w-0">
        <div className="truncate text-[13px] font-semibold text-slate-800">
          {item.grade} — {item.section} — {item.subject}
        </div>
        <div className="mt-0.5 text-[12px] text-slate-500">
          {item.teacher_name ? `${item.teacher_name} · ` : ""}
          {hasContent ? `${item.unit_count} units · ${item.chapter_count} chapters` : "Empty"}
        </div>
      </div>
      <button
        type="button"
        disabled={!hasContent}
        onClick={() => onImport(item)}
        className="shrink-0 rounded-lg border border-[#1565c0] px-3 py-1.5 text-[12px] font-semibold text-[#1565c0] hover:bg-[#f4f8fe] disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-300"
      >
        Import
      </button>
    </li>
  );
}

/** Side drawer: pick another class's plan to copy term content from. */
export default function CopyDrawer({ open, loading, error, plans, onClose, onImport }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const mine = plans.same_teacher || [];
  const others = plans.other_teachers || [];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Copy from another class">
      <button type="button" className="absolute inset-0 bg-slate-900/35" aria-label="Close" onClick={onClose} />
      <div className="relative flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl font-[poppins]">
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <h3 className="m-0 text-[15px] font-bold text-slate-900">Copy from another class</h3>
            <p className="m-0 mt-0.5 text-[12px] text-slate-500">Imports term content only. Status, feedback and history are not copied.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
          {loading && <p className="py-10 text-center text-[13px] text-slate-500">Loading plans…</p>}
          {error && <p className="py-4 text-[13px] text-rose-700">{error}</p>}
          {!loading && !error && mine.length === 0 && others.length === 0 && (
            <p className="py-10 text-center text-[13px] text-slate-500">No other plans to copy from yet.</p>
          )}
          {mine.length > 0 && (
            <>
              <div className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">My other classes</div>
              <ul className="m-0 list-none p-0">{mine.map((i) => <PlanItem key={i.uid} item={i} onImport={onImport} />)}</ul>
            </>
          )}
          {others.length > 0 && (
            <>
              <div className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {mine.length ? "Other teachers' classes" : "Available plans"}
              </div>
              <ul className="m-0 list-none p-0">{others.map((i) => <PlanItem key={i.uid} item={i} onImport={onImport} />)}</ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
